import React, { useMemo, useState, useEffect, useRef } from 'react';
import { MapContainer, Polyline, CircleMarker, Popup, useMap } from 'react-leaflet';
import BaseMapLayer from './BaseMapLayer';
import { getRouteColor, getCoordinates } from '../Utils/flightHelpers';
import * as turf from '@turf/turf';
import 'leaflet/dist/leaflet.css';

const MapViewAdjuster = ({ selectedRoute }) => {
  const map = useMap();

  useEffect(() => {
    if (selectedRoute) {
      const originCoords = getCoordinates(selectedRoute.origin);
      const destCoords = getCoordinates(selectedRoute.destination);

      if (originCoords && destCoords) {
        const bounds = map.getBounds();
        const originInView = bounds.contains(originCoords);
        const destInView = bounds.contains(destCoords);

        // If either point is not in view, fit the route to the map
        if (!originInView || !destInView) {
          map.fitBounds([originCoords, destCoords], {
            padding: [50, 50],
            maxZoom: 8
          });
        }
      }
    }
  }, [selectedRoute, map]);

  return null;
};

// Helper function to calculate great circle route
const getGreatCircleRoute = (originCoords, destCoords) => {
  try {
    const [lat1, lon1] = originCoords;
    const [lat2, lon2] = destCoords;

    // Calculate the direct difference
    const diffDirect = lon2 - lon1;

    // Determine the shortest path direction
    let shouldGoWest = false;
    if (diffDirect > 180) {
      shouldGoWest = true;
    } else if (diffDirect < -180) {
      shouldGoWest = false;
    } else if (diffDirect > 0) {
      shouldGoWest = false;
    } else {
      shouldGoWest = true;
    }

    // For routes crossing the Pacific (going west from negative to positive longitude)
    // we need to use adjusted coordinates
    let adjustedLon1 = lon1;
    let adjustedLon2 = lon2;

    if (shouldGoWest && lon1 < 0 && lon2 > 0) {
      // Westward across Pacific: adjust by keeping lon2 and moving lon1 into 180-360 range
      adjustedLon1 = lon1 + 360;
    }

    // Convert from [lat, lon] to [lon, lat] for Turf.js
    const from = turf.point([adjustedLon1, lat1]);
    const to = turf.point([adjustedLon2, lat2]);

    // Use turf.greatCircle
    const greatCircle = turf.greatCircle(from, to);

    // IMPORTANT: greatCircle.geometry.coordinates is already an array of [lon, lat] pairs
    // We need to flatten if it's a MultiLineString, or use directly if LineString
    let coordinates;
    if (greatCircle.geometry.type === 'MultiLineString') {
      // Flatten the array of arrays
      coordinates = greatCircle.geometry.coordinates.flat();
    } else {
      coordinates = greatCircle.geometry.coordinates;
    }

    // Convert to [lat, lon] format for Leaflet
    const allPositions = coordinates.map(([lon, lat]) => [lat, lon]);

    // Split into segments where we cross the antimeridian
    const segments = [];
    let currentSegment = [allPositions[0]];

    for (let i = 1; i < allPositions.length; i++) {
      const prevLon = allPositions[i - 1][1];
      const currLon = allPositions[i][1];
      const lonDiff = Math.abs(currLon - prevLon);

      // If longitude jumps more than 180°, we've crossed the antimeridian
      if (lonDiff > 180) {
        // Close current segment
        segments.push(currentSegment);
        // Start new segment
        currentSegment = [allPositions[i]];
      } else {
        currentSegment.push(allPositions[i]);
      }
    }

    // Add the last segment
    if (currentSegment.length > 0) {
      segments.push(currentSegment);
    }

    // NOW normalize all coordinates to -180 to 180 range for rendering
    const normalizedSegments = segments.map(segment =>
      segment.map(([lat, lon]) => {
        let normalizedLon = lon;
        while (normalizedLon > 180) normalizedLon -= 360;
        while (normalizedLon < -180) normalizedLon += 360;
        return [lat, normalizedLon];
      })
    );

    // Return normalized segments
    return normalizedSegments;
  } catch (error) {
    // Fallback to direct line if calculation fails
    return [[originCoords, destCoords]];
  }
};

const FlightMap = ({ flights, selectedRoute, setSelectedRoute, mapCenter, mapZoom, filterStatus }) => {
  const [hoveredRoute, setHoveredRoute] = useState(null);
  const popupTimeoutRef = useRef(null);

  // Group flights by route
  const routeGroups = useMemo(() => {
    const groups = {};

    // When filterStatus is 'all', combine bidirectional routes
    const shouldCombineRoutes = filterStatus === 'all';

    flights.forEach(flight => {
      let routeKey;

      if (shouldCombineRoutes) {
        // Create a normalized route key (alphabetically sorted) to combine both directions
        const airports = [flight.origin, flight.destination].sort();
        routeKey = `${airports[0]}-${airports[1]}`;
      } else {
        // Keep directional routes for arrivals/departures
        routeKey = `${flight.origin}-${flight.destination}`;
      }

      if (!groups[routeKey]) {
        if (shouldCombineRoutes) {
          // For combined routes, use sorted airports
          const airports = [flight.origin, flight.destination].sort();
          groups[routeKey] = {
            origin: airports[0],
            destination: airports[1],
            originName: airports[0] === flight.origin ? flight.originName : flight.destinationName,
            destinationName: airports[1] === flight.destination ? flight.destinationName : flight.originName,
            flights: [],
            isBidirectional: true
          };
        } else {
          groups[routeKey] = {
            origin: flight.origin,
            destination: flight.destination,
            originName: flight.originName,
            destinationName: flight.destinationName,
            flights: [],
            isBidirectional: false
          };
        }
      }

      groups[routeKey].flights.push(flight);
    });

    return Object.values(groups);
  }, [flights, filterStatus]);

  // Get the most common status for route coloring
  const getRouteStatus = (routeFlights) => {
    const statusCounts = {};
    routeFlights.forEach(f => {
      const status = f.status || 'Unknown';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    // Return the most common status
    return Object.entries(statusCounts)
      .sort((a, b) => b[1] - a[1])[0][0];
  };

  // Clear popup timeout on unmount
  useEffect(() => {
    return () => {
      if (popupTimeoutRef.current) {
        clearTimeout(popupTimeoutRef.current);
      }
    };
  }, []);

  const handlePopupMouseEnter = (routeKey) => {
    // Clear any pending close timeout
    if (popupTimeoutRef.current) {
      clearTimeout(popupTimeoutRef.current);
      popupTimeoutRef.current = null;
    }
    setHoveredRoute(routeKey);
  };

  const handlePopupMouseLeave = (e, polylineRef) => {
    // Close the popup immediately when leaving
    if (polylineRef) {
      polylineRef.closePopup();
    }
    setHoveredRoute(null);
  };

  return (
    <div className="flex-1 relative w-full h-full">
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        style={{ height: '100%', width: '100%', position: 'relative' }}
        scrollWheelZoom={true}
        className="h-full w-full"
        zoomControl={false}
      >
        <MapViewAdjuster selectedRoute={selectedRoute} />
        <BaseMapLayer />

        {routeGroups.map((route, idx) => {
          const originCoords = getCoordinates(route.origin);
          const destCoords = getCoordinates(route.destination);


          if (!originCoords || !destCoords) {
            if ((route.origin === 'SIN' && route.destination === 'LAX') ||
                (route.origin === 'LAX' && route.destination === 'SIN')) {
              console.log('Missing coordinates for Singapore-LA!');
            }
            return null;
          }

          const routeKey = `${route.origin}-${route.destination}`;
          const flightCount = route.flights.length;
          const dominantStatus = getRouteStatus(route.flights);

          // Calculate great circle route (returns array of segments)
          const routeSegments = getGreatCircleRoute(originCoords, destCoords);

          // Check if this route is selected - need to handle bidirectional routes
          const isSelected = selectedRoute && (
            (selectedRoute.origin === route.origin && selectedRoute.destination === route.destination) ||
            (route.isBidirectional && selectedRoute.origin === route.destination && selectedRoute.destination === route.origin)
          );

          const routeColor = isSelected ? 'rgba(251, 191, 36, 0.9)' : getRouteColor(dominantStatus);

          // Shared marker style for both origin and destination
          const markerStyle = isSelected
            ? { radius: 7, fillColor: '#1d4ed8', color: '#ffffff', weight: 2, fillOpacity: 1 }
            : { radius: 4, fillColor: '#ffffff', color: '#6b7280', weight: 1.5, fillOpacity: 1 };

          return (
            <React.Fragment key={`${routeKey}-${isSelected}`}>
              {routeSegments.map((segment, segmentIdx) => (
                <React.Fragment key={`${routeKey}-seg-${segmentIdx}`}>
                  {/* Glow layer */}
                  <Polyline
                    positions={segment}
                    pathOptions={{
                      color: routeColor,
                      weight: isSelected ? 20 : 14,
                      opacity: isSelected ? 0.15 : 0.1,
                      lineCap: 'round',
                      lineJoin: 'round',
                    }}
                    interactive={false}
                  />
                  {/* Main animated line */}
                  <Polyline
                    ref={(ref) => {
                      if (segmentIdx === 0 && ref) {
                        // Store reference to first segment for popup control
                        route._polylineRef = ref;
                      }
                    }}
                    positions={segment}
                    pathOptions={{
                      color: routeColor,
                      weight: isSelected ? 4 : 2.5,
                      opacity: isSelected ? 1 : 0.85,
                      ...(isSelected ? {} : { dashArray: '10 7', className: 'route-line-animated' }),
                      lineCap: 'round',
                      lineJoin: 'round',
                    }}
                    pane={isSelected ? 'markerPane' : 'overlayPane'}
                    eventHandlers={{
                      click: () => {
                        setSelectedRoute(route);
                      },
                      mouseover: (e) => {
                        if (!isSelected) {
                          e.target.setStyle({ weight: 6, opacity: 0.7 });
                        }
                        handlePopupMouseEnter(routeKey);
                        e.target.openPopup(e.latlng);
                      },
                      mouseout: (e) => {
                        // Don't close immediately - let popup's mouseenter save it
                        popupTimeoutRef.current = setTimeout(() => {
                          if (hoveredRoute !== routeKey) {
                            e.target.closePopup();
                          }
                        }, 100);

                        if (!isSelected) {
                          e.target.setStyle({
                            weight: 2.5,
                            opacity: 0.85
                          });
                        }
                      }
                    }}
                  >
                    {/* Only show popup on first segment */}
                    {segmentIdx === 0 && (
                      <Popup autoPan={false} closeButton={false} className="route-popup">
                        <div
                          onMouseEnter={() => handlePopupMouseEnter(routeKey)}
                          onMouseLeave={(e) => handlePopupMouseLeave(e, route._polylineRef)}
                        >
                          <div style={{ fontWeight: '700', fontSize: '15px', color: '#111111', marginBottom: '6px' }}>
                            {route.isBidirectional ? `${route.origin} ↔ ${route.destination}` : `${route.origin} → ${route.destination}`}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                            {route.originName} {route.isBidirectional ? '↔' : '→'} {route.destinationName}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                            {flightCount} {flightCount === 1 ? 'flight' : 'flights'} · <span style={{ color: getRouteColor(dominantStatus), fontWeight: '600' }}>{dominantStatus}</span>
                          </div>
                          <div
                            style={{ fontSize: '11px', color: '#0891b2', cursor: 'pointer', fontWeight: '500' }}
                            onClick={(e) => { e.stopPropagation(); setSelectedRoute(route); }}
                          >
                            View route details →
                          </div>
                        </div>
                      </Popup>
                    )}
                  </Polyline>
                </React.Fragment>
              ))}

              {/* Origin Airport Marker */}
              <CircleMarker
                center={originCoords}
                radius={markerStyle.radius}
                fillColor={markerStyle.fillColor}
                color={markerStyle.color}
                weight={markerStyle.weight}
                opacity={1}
                fillOpacity={markerStyle.fillOpacity}
              />

              {/* Destination Airport Marker */}
              <CircleMarker
                center={destCoords}
                radius={markerStyle.radius}
                fillColor={markerStyle.fillColor}
                color={markerStyle.color}
                weight={markerStyle.weight}
                opacity={1}
                fillOpacity={markerStyle.fillOpacity}
              />
            </React.Fragment>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default FlightMap;
